import { useState } from 'preact/hooks';
import { DEFAULT_RULES, MAX_PLAYERS, MIN_PLAYERS, type RulesConfig } from '../engine';
import { loadLastSetup, loadLocalGame, newLocalGame, saveLastSetup, saveLocalGame, useLocalGame, type LocalSave, type LocalSetup } from './local-game';
import { RulesForm } from './rules-form';
import { Table } from './table';

const DEFAULT_NAMES = ['You', 'Ann', 'Bob', 'Cy', 'Di', 'Ed', 'Flo', 'Gus'];

function randomSeed(): number {
  return crypto.getRandomValues(new Uint32Array(1))[0]!;
}

function seedFromUrl(): number | null {
  const raw = new URLSearchParams(location.search).get('seed');
  const n = raw === null ? NaN : Number(raw);
  return Number.isSafeInteger(n) && n >= 0 ? n : null;
}

/** Pads a remembered list back out to every seat, so changing the player count keeps working. */
function padded<T>(saved: readonly T[] | undefined, fallback: readonly T[]): T[] {
  return fallback.map((d, i) => saved?.[i] ?? d);
}

function Setup({ onStart }: { onStart: (setup: LocalSetup) => void }) {
  // Starts from the last table set up here; the first time, it's you plus four computers.
  // (Tests clear storage, so they always get the defaults.)
  const last = loadLastSetup();
  const [count, setCount] = useState(last?.names.length ?? 5);
  const [names, setNames] = useState(padded(last?.names, DEFAULT_NAMES));
  const [bots, setBots] = useState(padded(last?.bots, DEFAULT_NAMES.map((_, i) => i !== 0)));
  const [rules, setRules] = useState<RulesConfig>({ ...DEFAULT_RULES, ...last?.rules });
  const [privacy, setPrivacy] = useState(last?.privacy ?? true);

  const start = (e: Event) => {
    e.preventDefault();
    // "You" only makes sense for a person; a computer in that seat gets a name of its own.
    const chosen = names.slice(0, count).map((n, i) => {
      const name = n.trim() || DEFAULT_NAMES[i]!;
      return bots[i] && name === 'You' ? 'Al' : name;
    });
    onStart({ names: chosen, bots: bots.slice(0, count), rules, seed: seedFromUrl() ?? randomSeed(), privacy });
  };
  const humans = bots.slice(0, count).filter((b) => !b).length;

  return (
    <form class="panel setup" onSubmit={start}>
      <h2>Play against the computer</h2>
      <p class="muted small">Everything runs on this device. Switch seats to Person to share it with others.</p>
      <label class="row">
        <span>Players</span>
        <select value={count} data-testid="player-count" onChange={(e) => setCount(Number((e.target as HTMLSelectElement).value))}>
          {Array.from({ length: MAX_PLAYERS - MIN_PLAYERS + 1 }, (_, i) => i + MIN_PLAYERS).map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </label>
      {names.slice(0, count).map((name, i) => (
        <div class="row" key={i}>
          <input
            value={name}
            maxLength={20}
            aria-label={`Seat ${i + 1} name`}
            data-testid={`name-${i}`}
            onInput={(e) => setNames(names.map((n, j) => (j === i ? (e.target as HTMLInputElement).value : n)))}
          />
          <button
            type="button"
            class={bots[i] ? 'secondary small' : 'small'}
            data-testid={`kind-${i}`}
            onClick={() => setBots(bots.map((b, j) => (j === i ? !b : b)))}
          >
            {bots[i] ? 'Computer' : 'Person'}
          </button>
        </div>
      ))}
      <RulesForm rules={rules} onChange={setRules} />
      {humans > 1 && (
        <label class="row">
          <input type="checkbox" checked={privacy} data-testid="privacy" onChange={(e) => setPrivacy((e.target as HTMLInputElement).checked)} />
          <span>Hide hands between people</span>
        </label>
      )}
      <button type="submit" class="primary" data-testid="start-local">
        {humans === 0 ? 'Watch the computers play' : 'Deal'}
      </button>
    </form>
  );
}

function LocalGame({ initial, onExit, onRematch }: { initial: LocalSave; onExit: () => void; onRematch: () => void }) {
  const client = useLocalGame(initial);
  const fast = !!client.save.setup.fast;
  const [revealed, setRevealed] = useState<number | null>(client.seat);
  const humans = client.bots.filter((b) => !b).length;
  const gated =
    client.save.setup.privacy && humans > 1 && revealed !== client.seat && client.view.phase.kind !== 'gameOver';

  if (gated) {
    return (
      <div class="panel pass-device" data-testid="pass-device">
        <h2>Pass to {client.names[client.seat!]}</h2>
        <p class="muted">Hand the device over, then tap to see your cards.</p>
        <button class="primary" data-testid="reveal" onClick={() => setRevealed(client.seat)}>
          I'm {client.names[client.seat!]}
        </button>
      </div>
    );
  }
  return (
    <Table
      client={client}
      onExit={onExit}
      onRematch={onRematch}
      extraControls={
        <button class="link" data-testid="speed" aria-pressed={fast} onClick={() => client.setFast(!fast)}>
          {fast ? '⏩ Computers: fast' : '▶ Computers: normal'}
        </button>
      }
    />
  );
}

export function LocalApp() {
  const [save, setSave] = useState<LocalSave | null>(loadLocalGame);
  // Bumped by "same table again", which can repeat a seed, so the game remounts fresh.
  const [round, setRound] = useState(0);
  if (!save) {
    return (
      <Setup
        onStart={(setup) => {
          saveLastSetup(setup);
          const s = newLocalGame(setup);
          saveLocalGame(s);
          setSave(s);
        }}
      />
    );
  }
  return (
    <LocalGame
      key={`${round}-${save.setup.seed}`}
      initial={save}
      onExit={() => {
        saveLocalGame(null);
        setSave(null);
      }}
      onRematch={() => {
        // Same seats and rules, a new deal (a seeded test game stays seeded).
        const s = newLocalGame({ ...save.setup, seed: seedFromUrl() ?? randomSeed() });
        saveLocalGame(s);
        setSave(s);
        setRound(round + 1);
      }}
    />
  );
}
